<template>
  <header class="p-0">
    <div class="navbar-wrapper col-lg-12 m-0 p-0" ref="navbar-wrapper">
      <nav
        class="navbar navbar-expand-lg p-0"
        :class="{'justify-content-end' : removeLogo , 'justify-content-between' : !removeLogo}"
      >
        <div class="navbar-brand m-0 p-0">
          <a
            v-if="!removeLogo"
            :href="$t('website-link')"
            target="_blank"
          >
            <img
              :src="logo.imageSource"
              :width="logo.width"
              :height="logo.height"
              :alt="logo.alternateText"
            />
          </a>
        </div>
        <div 
          class="navbar-collapse expandedDiv" 
          ref='expandedDiv'
          id="navbar-menu"
          :aria-hidden="!isExpanded"
        >
          <ul class="navbar-nav">
            <li
              class="navbarLinks"
              v-for="field in fields"
              :key="field.fieldId"
            >
              <componentRenderer 
                v-bind:key="field.id" 
                :field="field" 
                class="underline"
                :class="{'menu-hidden': !isExpanded}"
              />
            </li>
          </ul>
        </div>
        <div class="expandButtonWrapper">
          <language-dropdown v-if="isHomeScreen" :languages="languages"/>
          <button
            class="navbar-toggler"
            type="button"
            @click="toggleMenu"
            @keydown.escape="closeMenu"
            :aria-expanded="isExpanded"
            aria-controls="navbar-menu"
            :aria-label="$t('toggleMenu')"
          >
            <svg width="20" height="20" viewBox="0 0 16 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.661 11.385H14.57m-12.909-10H14.57 1.66Zm0 5H14.57 1.66Z" stroke="#1B1B1B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
      </nav>
    </div>
  </header>
</template>

<script>
import componentRenderer from "@/components/component-renderer";
import { mapGetters } from "vuex";

export default {
  name: "navbar",
  components: { componentRenderer },
  props: {
    showLine: {
      required: false,
      type: Boolean
    },
    borderColor: {
      type: String,
      default: "#CCE4FF"
    },
    backgroundColor: {
      required: false,
      type: String,
      default: "#fff"
    },
    title: {
      required: false,
      type: String,
      default: "Hello world"
    },
    logo: {
      required: true,
      type: Object
    },
    fields: {
      required: false,
      type: Array,
      default: () => []
    },
    color: {
      required: false,
      type: String,
      default: "#000"
    },
    languages: {
      type: Array
    }
  },
  data() {
    return {
      isExpanded: false,
      windowWidth: window.innerWidth
    }
  },
  mounted () {
    // this.initializeTabs();
    this.$refs['navbar-wrapper'].style.color = this.color;
    if (this.showLine) {
      this.$el.parentNode.style.borderBottom= `1px solid ${this.borderColor}`
    }
    this.$el.parentNode.style.backgroundColor= `${this.backgroundColor}`
    
    // Listen for window resize to reset menu state
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  computed: {
    removeLogo () {
      return this.$store.getters.removeLogo;
    },
    isHomeScreen () {
      const path = location.pathname; // path format "/countryCode/pageName"
      const pageName = path.split("/")[2] // [1] is country code, [2] is pageName
      if (!pageName) return true // if there's no pageName meaning we're on the home page, show the flag.
      return false // if pageName exists, hide the flag.
    },
    isMobileView() {
      return this.windowWidth <= 991;
    }
  },
  watch: {
    isMobileView(newValue) {
      // Reset menu state when switching between mobile and desktop
      if (!newValue && this.isExpanded) {
        this.isExpanded = false;
        const expandedDiv = this.$refs['expandedDiv'];
        if (expandedDiv) {
          expandedDiv.style.maxHeight = null;
        }
      }
    }
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    toggleMenu() {
      const expandedDiv = this.$refs['expandedDiv'];
      
      if (this.isExpanded) {
        // Close menu
        this.closeMenu();
      } else {
        // Open menu
        this.isExpanded = true;
        expandedDiv.style.maxHeight = expandedDiv.scrollHeight + "px";
      }
    },
    closeMenu() {
      if (this.isExpanded) {
        const expandedDiv = this.$refs['expandedDiv'];
        expandedDiv.style.maxHeight = null;
        this.isExpanded = false;
      }
    },
    // Keep old method name for backward compatibility
    checkBoxClicked() {
      this.toggleMenu();
    }
  }
};
</script>

<style lang="scss" scoped>

.navbarLinks {
  white-space: nowrap;
  justify-content: center;
  align-items: center;
  display: flex;
  min-width: 200px;
}

.underline {
  &:hover {
    text-decoration: underline;
  }
}

.navbar-wrapper {
  position: relative;
  min-height: 77px;
  display: flex;
  align-items: center;

  .navbar {
    width: 100%;
  }
}

.hr {
  margin-top: 8px;
  margin-bottom: 8px;
}
.navbar-collapse {
  flex-grow: initial;
}
.navbar-toggler {
    margin: 0 10px;
    border: none;
    &:focus {
      outline: 3px solid #0065D6;
      outline-offset: 2px;
    }
}

.expandedDiv {
  @media (max-width: 991px) {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease-out;
    -moz-transition: max-height 0.3s ease-out;; /* Firefox 4 */
    -webkit-transition: max-height 0.3s ease-out;;  /* Safari and Chrome */
    -o-transition: max-height 0.3s ease-out;;  /* Opera */
    
    // When menu is collapsed, hide from screen readers and keyboard
    &[aria-hidden="true"] {
      visibility: hidden;
      
      // Ensure all focusable elements are not in tab order
      a, button, input, select, textarea, [tabindex] {
        visibility: hidden;
      }
    }
    
    // When menu is expanded, ensure visibility
    &[aria-hidden="false"] {
      visibility: visible;
      
      a, button, input, select, textarea, [tabindex] {
        visibility: visible;
      }
    }
  }
}
.expandButtonWrapper {
  display: flex;
  flex-direction: row;
}
.navbar-brand {
  display: flex;
  flex: 1;
}
@media (max-width: 575px) {
  .navbar-brand > img {
    width: 145px !important;
  }
  .navbar-toggler {
    padding: 2px 6px;
  }
  .navbar-toggler-icon {
    width: 20px
  }
}

@media (max-width: 991px) {
  .navbar {
    display: flex;
    flex-wrap: wrap;
  }
  
  .navbar-brand {
    order: 1;
  }
  
  .expandButtonWrapper {
    order: 2;
    margin-left: auto;
  }
  
  .expandedDiv {
    order: 3;
    width: 100%;
  }
  
  .navbarLinks {
    justify-content: flex-end;
    margin: 0 22px;
    font-size: 14px;
    border-bottom: 1px solid var(--color-6);
  }
}
</style>
